import api from "../utils/axiosConfig";
import type { BetEntry, BetStatus } from "./game";

// ==================== Types ====================

export type VisibilityFlag = 0 | 1;

/** The board adds odds on top of the entry shape the bet slip already uses. */
export interface LastBuyTicketBet extends BetEntry {
  odds: number;
}

export interface LastBuyTicketData {
  bet_id: string;
  game_number: string;
  status: BetStatus;
  is_pending?: 0 | 1;
  is_fail?: 0 | 1;
  status_text?: string;
  total_stake: number;
  currency: string;
  created_at: string;
  bets: LastBuyTicketBet[];
}

export interface LastBuyTicketResponseData {
  is_show: VisibilityFlag;
  ticket: LastBuyTicketData | null;
}

export type LastBuyTicketApiResponse = {
  status: number;
  system: string;
  message: string;
  data: LastBuyTicketResponseData;
};

// ==================== Endpoint ====================

const ENDPOINT = "/me/ticket";

export type ApiTicket = {
  draw: {
    drawNo: number;
    periodCode: string;
    closesAt: string;
  } | null;
  bets: Array<{
    id: string;
    position: number;
    betType: string;
    stakeCents: number;
    oddsBasisPoints: number;
    status: string;
    createdAt: string;
  }>;
  totalStakeCents: number;
};

const toTicketStatus = (statuses: string[]): BetStatus => {
  if (statuses.some((status) => status === "won")) return "WON";
  if (statuses.every((status) => status === "lost")) return "LOST";
  if (statuses.every((status) => status === "voided" || status === "refunded")) return "VOID";
  return "PENDING";
};

export const toTicket = (ticket: ApiTicket): LastBuyTicketResponseData => {
  if (!ticket.draw || !ticket.bets.length) return { is_show: 0, ticket: null };

  return {
    is_show: 1,
    ticket: {
      bet_id: ticket.bets[0].id,
      game_number: ticket.draw.periodCode,
      status: toTicketStatus(ticket.bets.map((bet) => bet.status)),
      total_stake: ticket.totalStakeCents / 100,
      currency: "MYR",
      created_at: ticket.bets[0].createdAt,
      bets: ticket.bets.map((bet) => ({
        position: bet.position,
        option: bet.betType.toUpperCase() as BetEntry["option"],
        amount: bet.stakeCents / 100,
        odds: bet.oddsBasisPoints / 10000,
      })),
    },
  };
};

export const LAST_BUY_TICKET_QUERY_KEY = ["last-buy-ticket"];

/** Backend owns visibility: the card renders only when is_show is 1 AND a
 *  ticket came back. A missing flag is treated as hidden, never as shown. */
export const shouldShowLastBuyTicket = (
  data: LastBuyTicketResponseData | undefined
): boolean => data?.is_show === 1 && Boolean(data.ticket);

export const getLastBuyTicket = async (): Promise<LastBuyTicketApiResponse> => {
  try {
    const response = await api.get<ApiTicket>(ENDPOINT);
    return {
      status: 1,
      system: "bets",
      message: "Last ticket retrieved",
      data: toTicket(response.data),
    };
  } catch (error) {
    console.error("Get last buy ticket error:", error);
    throw error;
  }
};
